import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Download, LogOut, Trash2, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { http, aud, formatApiErrorDetail } from "@/lib/api";
import { ChangePassword } from "@/components/admin/ChangePassword";

const STATUSES = ["new", "contacted", "quoted", "won", "lost"];
const COLS = ["created_at", "name", "business", "email", "phone", "state", "monthly_bill", "sites", "ownership", "contact_pref", "status", "assigned_to", "updated_by", "updated_at", "message", "source"];

const toCsv = (rows) =>
  [COLS.join(","), ...rows.map((r) => COLS.map((c) => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");

const Stat = ({ label, value, testId }) => (
  <div className="rounded-2xl border border-white/10 bg-[var(--surface)] p-5">
    <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-slate-400">{label}</p>
    <p data-testid={testId} className="mt-1 font-display font-black text-4xl">{value}</p>
  </div>
);

const sel = "rounded-lg bg-white/5 border border-white/15 px-3 py-2 text-white";

const LeadRow = ({ l, staff, onUpdate, onDelete }) => (
  <tr data-testid={`lead-row-${l.id}`} className="border-t border-white/10 align-top">
    <td className="p-4 text-slate-400 whitespace-nowrap">{new Date(l.created_at).toLocaleDateString("en-AU")}</td>
    <td className="p-4"><p className="font-semibold text-white">{l.name}</p><p className="text-slate-400">{l.business || "—"}</p></td>
    <td className="p-4"><a href={`tel:${l.phone}`} className="block text-white hover:text-[var(--sun)]">{l.phone}</a><a href={`mailto:${l.email}`} className="block text-slate-300 hover:text-[var(--sun)] break-all">{l.email}</a></td>
    <td className="p-4 text-slate-300 whitespace-nowrap">{l.state || "—"}<br />{l.monthly_bill ? `${aud(l.monthly_bill)}/mo` : "—"} · {l.sites} site{l.sites > 1 ? "s" : ""}</td>
    <td className="p-4 text-slate-300 max-w-xs">{[l.ownership, l.contact_pref].filter(Boolean).join(" · ") || "—"}{l.message && <p className="mt-1 text-slate-400 line-clamp-3">{l.message}</p>}</td>
    <td className="p-4 space-y-2">
      <select data-testid={`lead-status-${l.id}`} value={l.status} onChange={(e) => onUpdate(l.id, { status: e.target.value })} className={`${sel} capitalize w-full`}>
        {STATUSES.map((s) => <option key={s} value={s} className="bg-[var(--ink)]">{s}</option>)}
      </select>
      <select data-testid={`lead-assign-${l.id}`} value={l.assigned_to || ""} onChange={(e) => onUpdate(l.id, { assigned_to: e.target.value || null })} className={`${sel} w-full`}>
        <option value="" className="bg-[var(--ink)]">Unassigned</option>
        {staff.map((s) => <option key={s.id} value={s.name} className="bg-[var(--ink)]">{s.name}</option>)}
      </select>
      {l.updated_by && <p data-testid={`lead-updated-${l.id}`} className="text-xs text-slate-400">Updated by {l.updated_by}<br />{new Date(l.updated_at).toLocaleString("en-AU", { dateStyle: "short", timeStyle: "short" })}</p>}
    </td>
    <td className="p-4"><button data-testid={`lead-delete-${l.id}`} onClick={() => onDelete(l.id)} className="text-slate-400 hover:text-red-400 transition-colors" aria-label="Delete lead"><Trash2 size={18} /></button></td>
  </tr>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [staff, setStaff] = useState([]);

  useEffect(() => {
    if (user === false) nav("/admin/login", { replace: true });
  }, [user, nav]);

  const load = useCallback(async () => {
    try {
      const [{ data }, s] = await Promise.all([http.get("/leads"), http.get("/staff")]);
      setLeads(Array.isArray(data) ? data : []);
      setStaff(Array.isArray(s.data) ? s.data.filter((u) => u.role === "staff") : []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const onUpdate = async (id, patch) => {
    try {
      const { data } = await http.patch(`/leads/${id}`, patch);
      setLeads((ls) => ls.map((l) => (l.id === id ? data : l)));
      toast.success(patch.status ? `Marked as ${patch.status}` : `Assigned to ${patch.assigned_to || "nobody"}`);
    } catch (e) { toast.error("Update failed"); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try { await http.delete(`/leads/${id}`); setLeads((ls) => ls.filter((l) => l.id !== id)); toast.success("Lead deleted"); } catch (e) { toast.error("Delete failed"); }
  };

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return leads.filter((l) => (filter === "all" || (filter === "mine" ? l.assigned_to === user?.name : filter === "unassigned" ? !l.assigned_to : l.status === filter)) && (!t || [l.name, l.business, l.email, l.phone, l.state, l.assigned_to].join(" ").toLowerCase().includes(t)));
  }, [leads, q, filter, user]);

  const exportCsv = () => {
    const url = URL.createObjectURL(new Blob([toCsv(shown)], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "carwash-solar-leads.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  if (!user) return <div className="min-h-screen bg-[var(--ink)] grid place-items-center"><Loader2 className="animate-spin text-[var(--sun)]" /></div>;

  const count = (s) => leads.filter((l) => l.status === s).length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[var(--ink)]">
      <header className="border-b border-white/10">
        <div className="mx-auto max-w-[1440px] px-5 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="admin-home-link"><img src="/images/logo-light.png" alt="Carwash Solar + Batteries" className="h-12 w-auto" /></Link>
          <div className="flex items-center gap-5">
            <ChangePassword />
            <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"><LogOut size={18} /> <span className="hidden sm:inline">Sign out</span></button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1440px] px-5 lg:px-10 py-10">
        <p className="eyebrow">Signed in as {user.name} · {user.email}</p>
        <h1 className="mt-3 font-display font-black uppercase text-5xl sm:text-6xl">Leads</h1>
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Total" value={leads.length} testId="admin-stat-total" />
          <Stat label="New" value={count("new")} testId="admin-stat-new" />
          <Stat label="Contacted / quoted" value={count("contacted") + count("quoted")} testId="admin-stat-progress" />
          <Stat label="Won" value={count("won")} testId="admin-stat-won" />
        </div>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, business, email, phone, state" className="w-full rounded-xl bg-white/5 border border-white/15 pl-11 pr-4 py-3 text-white outline-none focus:border-[var(--sun)]" />
          </div>
          <select data-testid="admin-status-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-xl bg-white/5 border border-white/15 px-4 py-3 text-white capitalize">
            <option value="all" className="bg-[var(--ink)]">All leads</option>
            <option value="mine" className="bg-[var(--ink)]">Assigned to me</option>
            <option value="unassigned" className="bg-[var(--ink)]">Unassigned</option>
            {STATUSES.map((s) => <option key={s} value={s} className="bg-[var(--ink)]">{s}</option>)}
          </select>
          <button data-testid="admin-export-csv" onClick={exportCsv} className="flex items-center justify-center gap-2 rounded-xl bg-[var(--sun)] text-[var(--ink)] px-5 py-3 font-display font-bold uppercase tracking-wider"><Download size={16} /> Export CSV</button>
        </div>
        <div className="mt-6 overflow-x-auto rounded-2xl border border-white/10 bg-[var(--surface)]">
          <table data-testid="admin-leads-table" className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="text-left font-mono text-[11px] tracking-[0.2em] uppercase text-slate-400">
                <th className="p-4">Date</th><th className="p-4">Name</th><th className="p-4">Contact</th><th className="p-4">Site</th><th className="p-4">Details</th><th className="p-4">Status / owner</th><th className="p-4" />
              </tr>
            </thead>
            <tbody>
              {shown.map((l) => <LeadRow key={l.id} l={l} staff={staff} onUpdate={onUpdate} onDelete={onDelete} />)}
              {!shown.length && <tr><td colSpan={7} data-testid="admin-leads-empty" className="p-10 text-center text-slate-400">No leads yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
